import { useState, useEffect } from 'react';
import { UseFormSetValue, UseFormSetError, UseFormClearErrors } from 'react-hook-form';
import { Typography, Button, BorderedInput } from '@navi/web-ui/lib/primitives';
import { validateDPDRanges } from '../../utils';
import CrossIcon from '@cp/src/assets/icons/CrossIcon';
import ErrorIconOutline from '@cp/src/assets/icons/ErrorIconOutline';
import { DPDRangeField, ValidationResult, ConfigurationFormData } from '../../types';

interface DPDExclusionFieldProps {
  configurationData?: { dpdExclusion?: string };
  isDrawerOpen?: boolean;
  setValue: UseFormSetValue<ConfigurationFormData>;
  setError: UseFormSetError<ConfigurationFormData>;
  clearErrors: UseFormClearErrors<ConfigurationFormData>;
}

const DPDExclusionField = (props: DPDExclusionFieldProps) => {
  const { configurationData, isDrawerOpen, setValue, setError, clearErrors } = props;
  const [dpdRanges, setDpdRanges] = useState<Array<Record<DPDRangeField, string>>>([]);
  const [validation, setValidation] = useState<ValidationResult>({
    isValid: true,
    errors: [],
    errorMessage: ''
  });

  useEffect(() => {
    if (configurationData?.dpdExclusion) {
      const ranges = configurationData.dpdExclusion.split(',').map(range => {
        const [from, to] = range.trim().split('-');
        return {
          [DPDRangeField.From]: from?.trim() || '',
          [DPDRangeField.To]: to?.trim() || ''
        };
      });
      setDpdRanges(ranges);
      setValidation(validateDPDRanges(ranges));
    } else {
      setDpdRanges([]);
      setValidation({ isValid: true, errors: [], errorMessage: '' });
    }
  }, [configurationData?.dpdExclusion, isDrawerOpen]);

  const updateFormValue = (
    ranges: Array<Record<DPDRangeField, string>>,
    validationResult: ValidationResult
  ) => {
    const rangeString = ranges
      .map(r => `${r[DPDRangeField.From]}-${r[DPDRangeField.To]}`)
      .filter(r => r !== '-')
      .join(', ');
    setValue('dpdExclusion', rangeString, { shouldDirty: true });

    if (!validationResult.isValid) {
      setError('dpdExclusion', {
        type: 'manual',
        message: validationResult.errorMessage
      });
    } else {
      clearErrors('dpdExclusion');
    }
  };

  const handleAddRange = () => {
    const updatedRanges = [
      ...dpdRanges,
      {
        [DPDRangeField.From]: '',
        [DPDRangeField.To]: ''
      }
    ];
    const validationResult = validateDPDRanges(updatedRanges);
    setDpdRanges(updatedRanges);
    setValidation(validationResult);
    updateFormValue(updatedRanges, validationResult);
  };

  const validateRange = (from: string, to: string): boolean => {
    const fromNum = Number(from);
    const toNum = Number(to);
    return fromNum >= 1 && toNum <= 180 && fromNum <= toNum;
  };

  const handleRangeChange = (index: number, field: DPDRangeField, value: string) => {
    const updatedRanges = [...dpdRanges];
    updatedRanges[index][field] = value;
    const validationResult = validateDPDRanges(updatedRanges);
    setDpdRanges(updatedRanges);
    setValidation(validationResult);
    updateFormValue(updatedRanges, validationResult);
  };

  const handleRemoveRange = (index: number) => {
    const updatedRanges = dpdRanges.filter((_, i) => i !== index);
    const validationResult = validateDPDRanges(updatedRanges);
    setDpdRanges(updatedRanges);
    setValidation(validationResult);
    updateFormValue(updatedRanges, validationResult);
  };
  const hasAnyError = validation.errors.some(err => err);

  return (
    <div>
      {dpdRanges.map((range, index) => {
        const hasError = validation.errors[index];
        const isRangeInvalid =
          range[DPDRangeField.From] &&
          range[DPDRangeField.To] &&
          !validateRange(range[DPDRangeField.From], range[DPDRangeField.To]);

        return (
          <div key={index} className="flex items-end gap-4 mb-4">
            <div className="flex items-center flex-1">
              <Typography variant="p4" color="var(--navi-color-gray-c2)" className="mr-3">
                From
              </Typography>
              <BorderedInput
                value={range[DPDRangeField.From]}
                onChange={e => handleRangeChange(index, DPDRangeField.From, e.target.value)}
                fullWidth={true}
                error={hasError || isRangeInvalid}
              />
            </div>
            <div className="flex items-center flex-1">
              <Typography variant="p4" color="var(--navi-color-gray-c2)" className="mr-3">
                to
              </Typography>
              <BorderedInput
                fullWidth={true}
                value={range[DPDRangeField.To]}
                onChange={e => handleRangeChange(index, DPDRangeField.To, e.target.value)}
                error={hasError || isRangeInvalid}
              />
            </div>
            <Button
              type="button"
              variant="text"
              onClick={() => handleRemoveRange(index)}
              className="p-1 mb-4"
            >
              <CrossIcon size={20} fillColor={'var(--navi-color-gray-c2)'} />
            </Button>
          </div>
        );
      })}
      {hasAnyError && (
        <div className="flex items-center gap-1 text-red-500 mt-2 mb-3">
          <ErrorIconOutline width={14} height={14} />
          <Typography variant="p4" color="var(--navi-color-red-base)">
            {validation.errorMessage}
          </Typography>
        </div>
      )}
      <Button
        type="button"
        variant="secondary"
        size="small"
        onClick={handleAddRange}
        className="mt-2 h-9"
        fullWidth={true}
      >
        <Typography variant="h4" color="var(--navi-color-gray-c2)">
          + Add new range
        </Typography>
      </Button>
    </div>
  );
};

export default DPDExclusionField;
